import React from "react";
import { useTranslation } from "react-i18next";
import { getRiskLevelGradient, riskLevelBadgeColors, getRiskLevelPriority } from "../../../utils/riskLevelUtils";
import { levelDisplayNames } from "../../../utils/displayNameUtils";
import { getSubcategoryLabel } from "../../../utils/categoryLabels";

// Use global risk level utilities
const badgeColors = riskLevelBadgeColors;

const SectionCard = ({ children, title, icon }) => (
  <div style={{
    background: "linear-gradient(145deg, var(--card-bg), rgba(59,130,246,0.05))",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid var(--card-border)",
    boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div style={{
        width: "40px",
        height: "40px",
        borderRadius: "12px",
        background: "rgba(59,130,246,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "20px",
        color: "#3b82f6"
      }}>
        {icon}
      </div>
      <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const MetricChip = ({ label, value, color }) => (
  <div style={{
    padding: "10px 14px",
    borderRadius: "999px",
    border: `1px solid ${color}33`,
    background: `${color}14`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    flex: "1 1 0",
    minWidth: "160px"
  }}>
    <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)" }}>
      {label}
    </span>
    <span style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
      {value}
    </span>
  </div>
);

// Use global risk level utilities - create a helper object for backward compatibility
const riskGradients = {
  get critical() { return getRiskLevelGradient("critical"); },
  get high() { return getRiskLevelGradient("high"); },
  get medium() { return getRiskLevelGradient("medium"); },
  get low() { return getRiskLevelGradient("low"); },
  get very_low() { return getRiskLevelGradient("very_low"); },
  get default() { return getRiskLevelGradient("unknown"); },
};

const ReportRenderer = ({ data }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_detailed.aiDos" });
  const report = data || {};
  const summary = report?.summary || {};
  const breakdownEntries = Object.entries(report?.attack_type_breakdown || {});
  const detailedResults = Array.isArray(report?.detailed_results) ? report.detailed_results : [];

  const formatNumber = (value) => {
    if (typeof value !== "number") return t("notAvailable");
    if (value >= 1000) return value.toLocaleString();
    return Number.isInteger(value) ? value : value.toFixed(2);
  };

  const formatCost = (value) => {
    if (typeof value !== "number") return t("notAvailable");
    return `$${value.toFixed(2)}`;
  };

  const sortedResults = [...detailedResults].sort((a, b) => {
    if (a.passed !== b.passed) {
      return a.passed ? 1 : -1;
    }
    const aRisk = getRiskLevelPriority(a.risk_level);
    const bRisk = getRiskLevelPriority(b.risk_level);
    return aRisk - bRisk;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px 0 40px" }}>
      <SectionCard title={t("readinessOverview")} icon={<i className="fas fa-shield-alt"></i>}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{
            background: getRiskLevelGradient(summary.overall_risk_level),
            borderRadius: "12px",
            padding: "18px",
            color: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center"
          }}>
            <div style={{ opacity: 0.8, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em" }}>{t("overallRisk")}</div>
            <div style={{ fontSize: "28px", fontWeight: 700, marginTop: "4px" }}>
              {levelDisplayNames[summary.overall_risk_level] || t("notAvailable")}
            </div>
            <div style={{ marginTop: "12px", fontSize: "14px", opacity: 0.9 }}>
              {t("successfullyBlockedTests")}: {formatNumber(summary.tests_passed)} · {t("failedToBlock")}: {formatNumber(summary.tests_failed)}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
            <MetricChip label={t("totalCategoriesTested")} value={formatNumber(summary.total_tests)} color="#3b82f6" />
            <MetricChip label={t("totalRequests")} value={formatNumber(summary.total_requests)} color="#8b5cf6" />
            <MetricChip label={t("totalDurationSeconds")} value={formatNumber(summary.total_duration_seconds)} color="#f97316" />
            <MetricChip label={t("estimatedCost")} value={formatCost(summary.total_estimated_cost)} color="#22c55e" />
            <MetricChip label={t("apiErrorCount")} value={formatNumber(summary.api_error_count)} color="#ef4444" />
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t("attackTypeBreakdown")} icon={<i className="fas fa-chart-bar"></i>}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}>
          {breakdownEntries.map(([attackType, stats], index) => (
            <div key={attackType || `breakdown-${index}`} style={{
              border: "1px solid var(--card-border)",
              borderRadius: "12px",
              padding: "16px",
              background: "var(--card-bg)",
              display: "flex",
              flexDirection: "column",
              gap: "12px"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 600, color: "var(--card-text)" }}>
                  {attackType ? getSubcategoryLabel("llm_dos", attackType) : t("unknown")}
                </div>
                <span style={{
                  padding: "4px 10px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  background: "rgba(59,130,246,0.12)",
                  color: "#2563eb",
                  fontWeight: 600
                }}>
                  {stats.count || 0} {t("testsChip")}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", color: "var(--card-text)" }}>
                    <span>{t("securityReadiness")}</span>
                    <strong>{typeof stats?.success_rate === "number" ? (stats.success_rate * 100).toFixed(0) : "0"}%</strong>
                  </div>
                  <div style={{
                    width: "100%",
                    height: "8px",
                    background: "var(--card-border)",
                    borderRadius: "4px",
                    overflow: "hidden",
                    position: "relative"
                  }}>
                    <div style={{
                      width: `${typeof stats?.success_rate === "number" ? stats.success_rate * 100 : 0}%`,
                      height: "100%",
                      background: typeof stats?.success_rate === "number" ? (stats.success_rate >= 0.7 ? "#22c55e" : stats.success_rate >= 0.4 ? "#f59e0b" : "#ef4444") : "#ef4444",
                      borderRadius: "4px",
                      transition: "width 0.3s ease"
                    }} />
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "var(--card-text)" }}>
                  <span>{t("cost")}</span>
                  <strong>{formatCost(stats?.total_cost)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#ef4444" }}>
                  <span>{t("avgErrorRate")}</span>
                  <strong>{typeof stats?.avg_error_rate === "number" ? stats.avg_error_rate.toFixed(2) : t("notAvailable")}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title={t("detailedDosTestResults")} icon={<i className="fas fa-flask"></i>}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
          {sortedResults.map((test, index) => (
            <div key={test?.test_id || `test-${index}`} style={{
              border: "1px solid var(--card-border)",
              borderRadius: "16px",
              padding: "20px",
              background: "var(--card-bg)",
              display: "flex",
              flexDirection: "column",
              gap: "12px"
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <span style={{
                    padding: "6px 14px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "white",
                    background: test?.passed === true
                      ? "#22c55e"
                      : test?.passed === false
                        ? "#ef4444"
                        : "#6b7280"
                  }}>
                    {test?.passed === true
                      ? t("passed")
                      : test?.passed === false
                        ? t("failed")
                        : test?.passed === null || typeof test?.passed === "undefined"
                          ? t("unknown")
                          : String(test?.passed)}
                  </span>
                  <span style={{
                    padding: "6px 14px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "capitalize",
                    color: "white",
                    background: badgeColors[test?.risk_level] || "rgba(100, 116, 139, 0.4)"
                  }}>
                    {levelDisplayNames[test?.risk_level] || t("unknown")} {t("risk")}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ fontSize: "16px", fontWeight: 600, textTransform: "uppercase", color: "var(--card-text)", letterSpacing: "0.08em", lineHeight: "1" }}>
                    {test?.attack_type ? getSubcategoryLabel("llm_dos", test.attack_type) : t("unknownAttack")}
                  </div>
                  <div style={{ fontSize: "12px", color: "#9ca3af", lineHeight: "1" }}>
                    {test?.test_id || t("na")}
                  </div>
                </div>
              </div>
              {test?.details && (
                <p style={{ margin: 0, color: "var(--body-text)", lineHeight: 1.6 }}>
                  {test.details}
                </p>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
                <MetricChip label={t("durationSeconds")} value={formatNumber(test?.duration)} color="#6366f1" />
                <MetricChip label={t("avgResponseTime")} value={formatNumber(test?.avg_response_time)} color="#8b5cf6" />
                <MetricChip label={t("throughput")} value={formatNumber(test?.throughput)} color="#14b8a6" />
                <MetricChip label={t("costEstimate")} value={formatCost(test?.cost_estimate)} color="#f59e0b" />
                <MetricChip label={t("requestsSent")} value={formatNumber(test?.requests_sent)} color="#3b82f6" />
                <MetricChip label={t("successfullyBlocked")} value={formatNumber(test?.successful_responses)} color="#22c55e" />
                <MetricChip label={t("failedToBlock")} value={formatNumber(test?.failed)} color="#ef4444" />
                <MetricChip label={t("apiErrorResponses")} value={formatNumber(test?.api_error_responses)} color="#f97316" />
                <MetricChip label={t("errorRate")} value={formatNumber(test?.error_rate)} color="#ef4444" />
              </div>
            </div>
          ))}

          {!detailedResults.length && (
            <div style={{ textAlign: "center", padding: "40px", color: "var(--muted-text)" }}>
              {t("noDosTestExecutions")}
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
};

const AgenticAIDosReportTabDetails = ({ dosData, keyExists, isEmptyObject, hasExpectedStructure }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_detailed.aiDos" });

  if (!keyExists || isEmptyObject) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(107, 114, 128, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-file-alt" style={{ fontSize: "28px", color: "#6b7280" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("noReportAvailable")}
        </div>
      </div>
    );
  }

  if (!hasExpectedStructure) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(239, 68, 68, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: "28px", color: "#ef4444" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("invalidReport")}
        </div>
      </div>
    );
  }

  return <ReportRenderer data={dosData} />;
};

export default AgenticAIDosReportTabDetails;
